<template>
  <div class="container">
    <div class="filter-container">
      <!-- Distributor -->
      <div class="filter-item">
        <label>Distributor</label>
        <select
          v-model="selectedDistributorId"
          :disabled="isDistributorLocked || loadingFilters || loadingReport"
          @change="onDistributorChange"
        >
          <option :value="ALL">All Distributors</option>
          <option
            v-for="distributor in distributorList"
            :key="getDistributorId(distributor)"
            :value="String(getDistributorId(distributor))"
          >
            {{ getDistributorName(distributor) }}
          </option>
        </select>
      </div>

      <!-- Dealer -->
      <div class="filter-item">
        <label>Dealer</label>
        <select
          v-model="selectedDealerId"
          :disabled="isDealerDisabled"
          @change="handleDealerChange"
        >
          <option :value="ALL">
            {{ dealerPlaceholderText }}
          </option>

          <option
            v-for="dealer in dealerList"
            :key="getDealerId(dealer)"
            :value="String(getDealerId(dealer))"
          >
            {{ getDealerName(dealer) }}
          </option>
        </select>
      </div>

      <!-- Start Date -->
      <div class="filter-item date-item">
        <label>Start Date</label>
        <input
          type="date"
          v-model="startDate"
          :max="currentDate"
          :disabled="loadingReport"
          @change="handleDateChange"
        />
      </div>

      <!-- End Date -->
      <div class="filter-item date-item">
        <label>End Date</label>
        <input
          type="date"
          v-model="endDate"
          :max="currentDate"
          :disabled="loadingReport"
          @change="handleDateChange"
        />
      </div>

      <!-- Reason -->
      <div class="filter-item reason-item">
        <label>Reason</label>
        <select
          v-model="selectedReason"
          :disabled="loadingReport"
          @change="handleReasonChange"
        >
          <option value="discount">Discount</option>
        </select>
      </div>

      <!-- Search -->
      <div class="button-item">
        <button
          @click="performSearch"
          class="btn button-default"
          :disabled="isBusy"
        >
          {{ loadingReport ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="button-item">
        <button
          @click="exportToExcel"
          class="btn button-export"
          :disabled="isBusy || !reportData.length"
        >
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Filter loading -->
    <div v-if="loadingFilters" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i>
      <span> Loading filters...</span>
    </div>

    <!-- Summary -->
    <!-- <div v-if="!isBusy && reportData.length" class="summary-row">
      <div class="summary-card">
        <span class="summary-label">Invoices</span>
        <strong>{{ reportData.length }}</strong>
      </div>

      <div class="summary-card">
        <span class="summary-label">Items</span>
        <strong>{{ totalItemCount }}</strong>
      </div>

      <div class="summary-card">
        <span class="summary-label">Total Difference</span>
        <strong>{{ formatNumber(grandTotalDifference) }}</strong>
      </div>
    </div> -->

    <!-- Report loading -->
    <div v-if="loadingReport" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i>
      <span> Loading report...</span>
    </div>

    <!-- Table -->
    <div v-if="!isBusy && reportData.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr>
              <th>Serial No</th>
              <th>Dealer Name</th>
              <th>Invoice Number</th>
              <th>SKU / Item</th>
              <th class="text-end">Quantity Sold</th>
              <th class="text-end">Standard Price (LKR)</th>
              <th class="text-end">Actual Selling Price (LKR)</th>
              <th class="text-end">Price Difference (LKR)</th>
              <th class="text-end">Total Difference (LKR)</th>
            </tr>
          </thead>

          <tbody>
            <template
              v-for="(invoice, invoiceIndex) in reportData"
              :key="invoice.invoice_no || invoiceIndex"
            >
              <tr
                v-for="(item, itemIndex) in invoice.invoice_item"
                :key="`${invoice.invoice_no || invoiceIndex}-${itemIndex}`"
                :class="{ 'invoice-start-row': itemIndex === 0 }"
              >
                <td
                  v-if="itemIndex === 0"
                  :rowspan="invoice.invoice_item.length"
                  class="align-middle"
                >
                  {{ invoiceIndex + 1 }}
                </td>

                <td
                  v-if="itemIndex === 0"
                  :rowspan="invoice.invoice_item.length"
                  class="align-middle"
                >
                  {{ invoice.dealer_name || "-" }}
                </td>

                <td
                  v-if="itemIndex === 0"
                  :rowspan="invoice.invoice_item.length"
                  class="align-middle invoice-no"
                >
                  {{ invoice.invoice_no || "-" }}
                </td>

                <td>{{ item.item_name || "-" }}</td>

                <td class="text-end">
                  {{ formatQty(item.sold_qty) }}
                </td>

                <td class="text-end">
                  {{ formatNumber(item.standard_price) }}
                </td>

                <td class="text-end">
                  {{ formatNumber(item.actual_sold_price) }}
                </td>

                <td class="text-end">
                  {{ formatNumber(item.price_difference) }}
                </td>

                <td class="text-end total-difference">
                  {{ formatNumber(item.total_difference) }}
                </td>
              </tr>
            </template>
          </tbody>

          <tfoot>
            <tr>
              <td colspan="8" class="text-end footer-label">
                Grand Total (LKR)
              </td>
              <td class="text-end footer-total">
                {{ formatNumber(grandTotalDifference) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Red no data message -->
    <!-- <div
      v-if="!isBusy && searchedOnce && !reportData.length"
      class="no-data-message error-state"
    >
      <div class="error-icon">!</div>
      <div>
        <strong>No data found</strong>
        <p>No credit note data found for the selected filters.</p>
      </div>
    </div> -->
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";
import {
  fetchCreditNoteReport,
  fetchDistributorList,
  fetchDealersByDistributors,
} from "@/services/dataService";

export default {
  name: "creditNoteReportData",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      ALL,

      token: "",
      profileId: Number(localStorage.getItem("profile_id") || 0),
      roleKey: "OTHER",

      distributorList: [],
      dealerList: [],

      selectedDistributorId: ALL,
      selectedDealerId: ALL,

      isDistributorLocked: false,

      currentDate,
      startDate: currentDate,
      endDate: currentDate,

      selectedReason: "discount",

      loadingFilters: false,
      loadingDealers: false,
      loadingReport: false,

      searchedOnce: false,
      reportData: [],
    };
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  computed: {
    isBusy() {
      return this.loadingFilters || this.loadingDealers || this.loadingReport;
    },

    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    isDealerDisabled() {
      return (
        this.loadingFilters ||
        this.loadingDealers ||
        this.loadingReport ||
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      );
    },

    dealerPlaceholderText() {
      if (this.loadingDealers) return "Loading dealers...";
      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      ) {
        return "All Dealers";
      }

      return "All Dealers";
    },

    totalItemCount() {
      return this.reportData.reduce((total, invoice) => {
        return total + (invoice.invoice_item?.length || 0);
      }, 0);
    },

    grandTotalDifference() {
      return this.reportData.reduce((invoiceTotal, invoice) => {
        const itemTotal = (invoice.invoice_item || []).reduce((total, item) => {
          return total + this.toNumber(item.total_difference);
        }, 0);

        return invoiceTotal + itemTotal;
      }, 0);
    },
  },

  mounted() {
    this.initFilters();
  },

  methods: {
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      const labelUpper = String(labelFromStorage || "")
        .toUpperCase()
        .trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    async initFilters() {
      this.token = localStorage.getItem("token_set") || "";
      this.profileId = Number(localStorage.getItem("profile_id") || 0);
      this.initRoleFromStorage();

      if (!this.token) {
        this.toast.error("Login token not found. Please login again.");
        return;
      }

      if (!this.profileId) {
        this.toast.error("Profile ID not found. Please login again.");
        return;
      }

      this.loadingFilters = true;

      try {
        const response = await fetchDistributorList(
          this.profileId,
          "",
          this.token,
        );

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.distributorList = [];
          this.toast.error(body?.message || "Failed to load distributors.");
          return;
        }

        this.distributorList = this.listOf(body);

        if (this.isDistributorUser) {
          await this.lockDistributorForDistributorLogin();
        }
      } catch (error) {
        console.error("Distributor filter load error:", {
          status: error?.response?.status,
          data: error?.response?.data,
          error,
        });

        this.distributorList = [];
        this.toast.error(
          error?.response?.data?.message || "Failed to load distributors.",
        );
      } finally {
        this.loadingFilters = false;
      }
    },

    async lockDistributorForDistributorLogin() {
      if (!this.profileId) return;

      const ownDistributor = this.distributorList.find((distributor) => {
        return (
          Number(this.getDistributorId(distributor)) === Number(this.profileId)
        );
      });

      if (ownDistributor) {
        this.selectedDistributorId = String(
          this.getDistributorId(ownDistributor),
        );
      } else {
        this.selectedDistributorId = String(this.profileId);

        const businessName =
          localStorage.getItem("business_name") ||
          localStorage.getItem("full_name") ||
          "My Distributor";

        this.distributorList = [
          {
            profile_id: this.profileId,
            name: businessName,
          },
          ...this.distributorList,
        ];
      }

      this.isDistributorLocked = true;
      this.selectedDealerId = this.ALL;

      await this.loadDealersByDistributor();
    },

    async onDistributorChange() {
      this.selectedDealerId = this.ALL;
      this.dealerList = [];
      this.reportData = [];
      this.searchedOnce = false;

      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      ) {
        return;
      }

      await this.loadDealersByDistributor();
    },

    async loadDealersByDistributor() {
      if (
        !this.selectedDistributorId ||
        this.selectedDistributorId === this.ALL
      ) {
        this.dealerList = [];
        this.selectedDealerId = this.ALL;
        return;
      }

      this.loadingDealers = true;
      this.dealerList = [];
      this.selectedDealerId = this.ALL;

      try {
        const response = await fetchDealersByDistributors(
          this.selectedDistributorId,
          this.token,
        );

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.dealerList = [];
          this.toast.error(body?.message || "Failed to load dealers.");
          return;
        }

        const rawDealers = this.listOf(body);

        this.dealerList = rawDealers
          .map((dealer) => {
            return {
              ...dealer,

              // IMPORTANT:
              // Credit Note Report dealer_id must be dealer profile_id.
              // Never use app_users.id here.
              dealer_report_profile_id: String(dealer?.profile_id ?? ""),
            };
          })
          .filter((dealer) => dealer.dealer_report_profile_id);

        console.log(
          "Credit Note Dealer Dropdown:",
          this.dealerList.map((dealer) => ({
            name: this.getDealerName(dealer),
            app_user_id: dealer.id,
            profile_id: dealer.profile_id,
            dealer_id_for_report: dealer.dealer_report_profile_id,
          })),
        );
      } catch (error) {
        console.error("Dealer filter load error:", {
          status: error?.response?.status,
          data: error?.response?.data,
          url: error?.config?.url,
          method: error?.config?.method,
          error,
        });

        this.dealerList = [];
        this.selectedDealerId = this.ALL;

        this.toast.error(
          error?.response?.data?.message || "Failed to load dealers.",
        );
      } finally {
        this.loadingDealers = false;
      }
    },

    handleDealerChange() {
      this.reportData = [];
      this.searchedOnce = false;
    },

    handleDateChange() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.endDate = this.startDate;
      }

      this.reportData = [];
      this.searchedOnce = false;
    },

    handleReasonChange() {
      this.reportData = [];
      this.searchedOnce = false;
    },

    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";

      if (!this.token) {
        this.toast.error("Login token not found. Please login again.");
        return;
      }

      if (!this.startDate || !this.endDate) {
        this.toast.error("Please select start date and end date.");
        return;
      }

      if (this.startDate > this.endDate) {
        this.toast.error("End date cannot be before start date.");
        return;
      }

      this.loadingReport = true;
      this.searchedOnce = true;
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate,
          end_date: this.endDate,

          distributor_id:
            this.selectedDistributorId === this.ALL
              ? ""
              : String(this.selectedDistributorId),

          dealer_id: this.getDealerIdForReport(),

          reason: this.selectedReason,
        };

        const response = await fetchCreditNoteReport(this.token, payload);
        const responseData = response?.data;

        if (responseData?.status === true) {
          this.reportData = this.normalizeReportData(responseData.data || []);

          if (this.reportData.length) {
            this.toast.success("Credit note report loaded successfully.");
          } else {
            this.toast.error("No data found for the selected filters.");
          }
        } else {
          this.reportData = [];
          this.toast.error(responseData?.message || "No data found.");
        }
      } catch (error) {
        console.error("Credit Note Report API Error:", {
          status: error?.response?.status,
          data: error?.response?.data,
          error,
        });

        this.reportData = [];
        this.toast.error(this.getErrorMessage(error));
      } finally {
        this.loadingReport = false;
      }
    },

    normalizeReportData(data) {
      if (!Array.isArray(data)) return [];

      return data.map((invoice) => {
        const invoiceItems = Array.isArray(invoice.invoice_item)
          ? invoice.invoice_item
          : [];

        const normalizedItems = invoiceItems.map((item) => {
          const soldQty = this.toNumber(item.sold_qty);
          const priceDifference = this.toNumber(item.price_difference);
          const rowTotalDifference = soldQty * priceDifference;

          return {
            item_name: item.item_name || "",
            sold_qty: soldQty,
            standard_price: this.toNumber(item.standard_price),
            actual_sold_price: this.toNumber(item.actual_sold_price),
            price_difference: priceDifference,

            // Row-wise total, not API invoice total
            total_difference: rowTotalDifference,
          };
        });

        const invoiceTotalDifference = normalizedItems.reduce((total, item) => {
          return total + this.toNumber(item.total_difference);
        }, 0);

        return {
          invoice_no: invoice.invoice_no || "",
          dealer_name: invoice.dealer_name || "",

          // Calculated frontend total, not API value
          total_difference: invoiceTotalDifference,

          invoice_item: normalizedItems,
        };
      });
    },

    getDealerIdForReport() {
      if (
        this.selectedDistributorId === this.ALL ||
        this.selectedDealerId === this.ALL
      ) {
        return "";
      }

      const selectedDealer = this.dealerList.find((dealer) => {
        return (
          String(this.getDealerId(dealer)) === String(this.selectedDealerId)
        );
      });

      return selectedDealer
        ? String(selectedDealer.dealer_report_profile_id)
        : "";
    },

    bodyOf(response) {
      return response?.data ?? response;
    },

    listOf(body) {
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.data?.data)) return body.data.data;
      if (Array.isArray(body?.data)) return body.data;
      return [];
    },

    isSuccessResponse(body) {
      return (
        body?.status === true ||
        body?.status === "success" ||
        body?.status === "Success"
      );
    },

    getDistributorId(distributor) {
      return (
        distributor?.profile_id ??
        distributor?.id ??
        distributor?.ProfileId ??
        distributor?.profileId ??
        ""
      );
    },

    getDistributorName(distributor) {
      return (
        distributor?.name ||
        distributor?.business_name ||
        distributor?.distributor_name ||
        distributor?.full_name ||
        "-"
      );
    },

    getDealerId(dealer) {
      return String(
        dealer?.dealer_report_profile_id ?? dealer?.profile_id ?? "",
      );
    },

    getDealerName(dealer) {
      return (
        dealer?.name ||
        dealer?.dealer_name ||
        dealer?.business_name ||
        dealer?.full_name ||
        `${dealer?.first_name || ""} ${dealer?.last_name || ""}`.trim() ||
        "-"
      );
    },

    getSelectedDistributorName() {
      if (this.selectedDistributorId === this.ALL) return "All Distributors";

      const selected = this.distributorList.find((item) => {
        return (
          String(this.getDistributorId(item)) ===
          String(this.selectedDistributorId)
        );
      });

      return selected ? this.getDistributorName(selected) : "-";
    },

    getSelectedDealerName() {
      if (
        this.selectedDistributorId === this.ALL ||
        this.selectedDealerId === this.ALL
      ) {
        return "All Dealers";
      }

      const selected = this.dealerList.find((item) => {
        return String(this.getDealerId(item)) === String(this.selectedDealerId);
      });

      return selected ? this.getDealerName(selected) : "-";
    },

    getErrorMessage(error) {
      const status = error?.response?.status;
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.error;

      if (status === 422) {
        return (
          serverMessage || "Invalid filters. Please check the selected values."
        );
      }

      if (status === 429) {
        return "Too many requests. Please wait a few seconds and try again.";
      }

      if ([500, 502, 503, 504].includes(status)) {
        return (
          serverMessage || "Server error while loading Credit Note Report."
        );
      }

      if (serverMessage) {
        return serverMessage;
      }

      return "Something went wrong while loading Credit Note Report.";
    },

    toNumber(value) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },

    formatNumber(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatQty(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    getReasonLabel() {
      if (this.selectedReason === "discount") {
        return "Discount";
      }

      return this.selectedReason || "-";
    },

    exportToExcel() {
      if (!this.reportData.length) return;

      const headerRow = [
        "Serial No",
        "Dealer Name",
        "Invoice Number",
        "SKU / Item",
        "Quantity Sold",
        "Standard Price (LKR)",
        "Actual Selling Price (LKR)",
        "Price Difference (LKR)",
        "Total Difference (LKR)",
      ];

      const dataRows = [];

      this.reportData.forEach((invoice, invoiceIndex) => {
        invoice.invoice_item.forEach((item, itemIndex) => {
          dataRows.push([
            itemIndex === 0 ? invoiceIndex + 1 : "",
            itemIndex === 0 ? invoice.dealer_name || "" : "",
            itemIndex === 0 ? invoice.invoice_no || "" : "",
            item.item_name || "",
            this.toNumber(item.sold_qty),
            this.toNumber(item.standard_price),
            this.toNumber(item.actual_sold_price),
            this.toNumber(item.price_difference),
            this.toNumber(item.total_difference),
          ]);
        });
      });

      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = `${firstName} ${lastName}`.trim() || "-";
      const generatedAt = new Date().toLocaleString("en-US", { hour12: false });

      const aoa = [
        ["Credit Note Report"],
        ["Distributor", this.getSelectedDistributorName()],
        ["Dealer", this.getSelectedDealerName()],
        ["Reason", this.getReasonLabel()],
        ["Start Date", this.startDate || "-"],
        ["End Date", this.endDate || "-"],
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
        [],
        headerRow,
        ...dataRows,
        [],
        ["", "", "", "", "", "", "", "Grand Total (LKR)", this.grandTotalDifference],
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }];

      ws["!cols"] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 22 },
        { wch: 28 },
        { wch: 14 },
        { wch: 16 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
      ];

      if (ws["A1"]) {
        ws["A1"].s = {
          font: { bold: true, sz: 14 },
        };
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Credit_Note_Report");

      XLSX.writeFile(
        wb,
        `Credit_Note_Report_${this.startDate}_to_${this.endDate}.xlsx`,
      );

      this.toast.success("Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
.container {
  background: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 18px;
  margin: 10px;
  width: calc(100% - 20px);
  box-sizing: border-box;
}

/* Compact filter bar - less rounded, existing button colors */
.filter-container {
  display: grid;
  grid-template-columns:
    minmax(180px, 1.15fr)
    minmax(180px, 1.15fr)
    minmax(145px, 0.85fr)
    minmax(145px, 0.85fr)
    minmax(130px, 0.75fr)
    auto
    auto;
  align-items: end;
  gap: 12px;
  width: 100%;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.filter-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.date-item {
  min-width: 130px;
}

.reason-item {
  min-width: 120px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: #374151;
}

select,
input[type="date"] {
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  background: #ffffff;
  color: #111827;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}

select:focus,
input[type="date"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.12);
}

select:disabled,
input[type="date"]:disabled {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.button-item {
  display: flex;
  align-items: flex-end;
  min-width: max-content;
}

.btn.button-default,
.btn.button-export {
  min-height: 38px;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

/* Existing previous colors */
.btn.button-default {
  background-color: #007bff;
}

.btn.button-export {
  background-color: #28a745;
}

.btn.button-default:not(:disabled):hover,
.btn.button-export:not(:disabled):hover {
  transform: translateY(-1px);
  opacity: 0.92;
}

.btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.75;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.summary-card {
  min-width: 160px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.summary-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}

.summary-card strong {
  font-size: 18px;
  color: #111827;
}

.table-scroll {
  max-height: 520px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  margin-top: 10px;
  position: relative;
  border-radius: 6px;
  scrollbar-gutter: stable;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  position: sticky;
  top: 0;
  background: #f9fafb;
  z-index: 5;
  white-space: nowrap;
  font-size: 13px;
  color: #374151;
  vertical-align: middle;
}

.table tbody td {
  vertical-align: middle;
  font-size: 13px;
}

.invoice-start-row td {
  border-top: 2px solid #d1d5db;
}

.invoice-no {
  font-weight: 700;
  color: #1f2937;
}

.total-difference {
  font-weight: 800;
  color: #111827;
  background: #f8fafc;
}

.footer-label {
  font-weight: 800;
  background: #f9fafb;
}

.footer-total {
  font-weight: 900;
  background: #f9fafb;
}

.align-middle {
  vertical-align: middle !important;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.no-data-message {
  margin-top: 18px;
  padding: 18px 20px;
  font-size: 14px;
}

/* Red no-data state */
.error-state {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fef2f2;
  color: #991b1b;
}

.error-state strong {
  display: block;
  color: #7f1d1d;
  font-size: 15px;
  margin-bottom: 2px;
}

.error-state p {
  margin: 0;
  color: #b91c1c;
}

.error-icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #dc2626;
  color: #ffffff;
  font-weight: 900;
}

.text-end {
  text-align: right;
}

.card {
  border-radius: 6px;
  overflow: hidden;
}

/* Medium screens */
@media (max-width: 1280px) {
  .filter-container {
    grid-template-columns: repeat(4, minmax(150px, 1fr));
  }

  .button-item {
    min-width: 0;
  }

  .btn.button-default,
  .btn.button-export {
    width: 100%;
  }
}

@media (max-width: 820px) {
  .container {
    padding: 14px;
    border-radius: 6px;
  }

  .filter-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 12px;
    border-radius: 5px;
  }

  .button-item {
    width: 100%;
  }

  .btn.button-default,
  .btn.button-export {
    width: 100%;
  }
}

@media (max-width: 520px) {
  .filter-container {
    grid-template-columns: 1fr;
  }

  .summary-card {
    width: 100%;
  }
}

/* =========================
   Premium tiny scrollbars
   ========================= */

/* Firefox */
.container,
.container * {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.38) transparent;
}

/* Main table scroll */
.table-scroll::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.table-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.28);
  border-radius: 999px;
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.55);
}

.table-scroll::-webkit-scrollbar-corner {
  background: transparent;
}

/* Any inner scrollbars inside this component */
.container *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.container *::-webkit-scrollbar-track {
  background: transparent;
}

.container *::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.26);
  border-radius: 999px;
}

.container *::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.55);
}

.container *::-webkit-scrollbar-corner {
  background: transparent;
}
</style>
